import { useRef, useState } from 'react'
import { inferMediaType } from '@/lib/media'
import { Button } from '@/shared/ui/Button'

interface MediaPickerProps {
  label?: string
  description?: string
  allowMultiple?: boolean
  accept?: string
  cameraAccept?: string
  capture?: boolean | 'user' | 'environment'
  busyText?: string
  onFiles: (files: FileList | null) => void
  onLink: (url: string) => void
  variant?: 'inline' | 'sheet'
}

export function MediaPicker({
  label = 'Add media',
  description,
  allowMultiple = true,
  accept = 'image/*,video/*',
  cameraAccept = 'image/*,video/*',
  capture = false,
  busyText,
  onFiles,
  onLink,
  variant = 'inline',
}: MediaPickerProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const cameraInputRef = useRef<HTMLInputElement | null>(null)
  const [link, setLink] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)
  const [showSheet, setShowSheet] = useState(false)
  const disabled = !!busyText

  const handleLinkSubmit = () => {
    if (!link.trim()) return
    const maybeType = inferMediaType(link)
    if (!maybeType) {
      setLinkError('Unknown file type — continuing as image')
    } else {
      setLinkError(null)
    }
    onLink(link.trim())
    setLink('')
    setShowSheet(false)
  }

  const openPicker = (camera = false) => {
    if (camera) {
      cameraInputRef.current?.click()
      return
    }
    fileInputRef.current?.click()
  }

  const actions = (
    <div className="flex flex-col gap-2">
      {/* URL Input + Add Button - Single Row */}
      <div className="flex gap-2 w-full">
        <input
          className="form-field flex-1"
          placeholder="Paste a link (YouTube, image, PDF)"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleLinkSubmit()
          }}
        />
        <Button variant="secondary" size="sm" onClick={handleLinkSubmit} disabled={disabled}>
          Add
        </Button>
      </div>

      {/* Photo/Video Button - Single Row */}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => openPicker(true)}
        disabled={disabled}
        className="w-full"
      >
        {busyText || 'Take photo / video'}
      </Button>

      {/* Library Button - Single Row */}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => openPicker(false)}
        disabled={disabled}
        className="w-full"
      >
        {busyText || 'Choose from library'}
      </Button>
    </div>
  )

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={allowMultiple}
        className="hidden"
        onChange={(e) => {
          onFiles(e.target.files)
          e.target.value = ''
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept={cameraAccept}
        className="hidden"
        capture={capture || undefined}
        onChange={(e) => {
          onFiles(e.target.files)
          e.target.value = ''
        }}
      />

      {label && (
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-gray-700 dark:text-slate-300">{label}</p>
          {description && <p className="text-xs text-gray-600 dark:text-slate-400">{description}</p>}
        </div>
      )}

      {variant === 'inline' && <div>{actions}</div>}

      {variant === 'sheet' && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowSheet((prev) => !prev)}
          disabled={disabled}
        >
          {busyText || 'Add media'}
        </Button>
      )}

      {variant === 'sheet' && showSheet && (
        <div className="rounded-xl border border-white/10 bg-slate-900/90 p-3 space-y-2">
          {actions}
          <Button variant="secondary" size="sm" onClick={() => setShowSheet(false)} className="w-full">
            Done
          </Button>
        </div>
      )}

      {linkError && (
        <p className="text-xs text-amber-600 dark:text-amber-300">{linkError}</p>
      )}
    </div>
  )
}
